// frontend/src/components/AssetQRScannerModal.js
/**
 * Asset QR Scanner Modal – in-app scan for asset-admin.
 * Camera + file fallback; parse aligned with backend sanitize_asset_tag;
 * double-submit guard, lifecycle, file size cap, facingMode.
 * 
 * File upload is conditionally displayed:
 * - Hidden by default when camera works (progressive disclosure)
 * - Shown prominently when camera fails
 * - Available on-demand via "Can't use camera?" link
 * - Always visible on desktop (no camera hardware)
 */

import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import Modal from './UI/Modal';
import { Upload, AlertCircle, Camera } from 'lucide-react';
import { assetInventoryAPI } from '../services/api';
import toast from '../utils/toast';

const SCANNER_DIV_ID = 'asset-qr-scanner';
const MAX_FILE_BYTES = 5 * 1024 * 1024;

/**
 * Detect if device is likely desktop (no camera hardware)
 * @returns {boolean}
 */
function isDesktopDevice() {
  // Check for mobile user agents
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobile = mobileRegex.test(navigator.userAgent);
  
  // Desktop: not a mobile user agent
  // Note: Some tablets may be detected as desktop, but they typically have cameras
  // This is a heuristic - actual camera availability is checked via camera API
  return !isMobile;
}

/**
 * Parse tag from scanned string. Align with backend: 1–50 chars, ^[A-Za-z0-9_-]+$
 * @param {string} raw
 * @returns {string|null}
 */
function parseTagFromScanResult(raw) {
  if (!raw || typeof raw !== 'string') return null;
  let s = raw.trim();
  const fromUrl = s.match(/[?&]tag=([^&]+)/);
  if (fromUrl) {
    try {
      s = decodeURIComponent(fromUrl[1]).trim();
    } catch {
      return null;
    }
  }
  if (s.length < 1 || s.length > 50) return null;
  if (!/^[A-Za-z0-9_-]+$/.test(s)) return null;
  return s;
}

const AssetQRScannerModal = ({ isOpen, onClose, onScanSuccess }) => {
  const [cameraError, setCameraError] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [showUploadOption, setShowUploadOption] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const scannerRef = useRef(null);
  const fileScannerRef = useRef(null);
  const startedRef = useRef(false);
  const handledRef = useRef(false);
  const fileInputRef = useRef(null);

  const handleDecode = async (decodedText) => {
    if (handledRef.current) return;
    const tag = parseTagFromScanResult(decodedText);
    if (!tag) {
      toast.error('Unrecognized QR code format');
      return;
    }
    handledRef.current = true;
    setLookupLoading(true);
    try {
      const { data, status } = await assetInventoryAPI.lookupByTag(tag).then(
        (r) => ({ data: r.data, status: r.status }),
        (e) => ({ data: e?.response?.data, status: e?.response?.status })
      );
      if (status === 200) {
        toast.success('Asset found');
        await stopScanner();
        onScanSuccess(data);
        onClose();
      } else if (status === 410) {
        toast.success('Asset found (disposed)');
        await stopScanner();
        onScanSuccess(data?.asset || data);
        onClose();
      } else {
        toast.error(data?.error || 'Asset not found');
        handledRef.current = false;
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Asset not found');
      handledRef.current = false;
    } finally {
      setLookupLoading(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current && startedRef.current) {
      try {
        await scannerRef.current.stop();
      } catch (_) {}
      scannerRef.current = null;
      startedRef.current = false;
    }
    
    // Clean up file scanner instance if it exists
    if (fileScannerRef.current) {
      try {
        // File scanner doesn't need stop(), but clear reference
        fileScannerRef.current = null;
      } catch (_) {}
    }
  };

  // Detect desktop on mount
  useEffect(() => {
    setIsDesktop(isDesktopDevice());
  }, []);

  // Start camera when opened; stop on close or route change
  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setShowUploadOption(false);
      setCameraError(false);
      setLookupLoading(false);
      handledRef.current = false;
      return;
    }

    // Reset state when modal opens
    handledRef.current = false;
    setCameraError(false);
    setLookupLoading(false);
    setShowUploadOption(isDesktop); // Show upload on desktop by default

    const div = document.getElementById(SCANNER_DIV_ID);
    if (!div) return;

    // On desktop, skip camera initialization
    if (isDesktop) {
      setCameraError(true);
      return;
    }

    const html5 = new Html5Qrcode(SCANNER_DIV_ID);
    scannerRef.current = html5;

    const config = { fps: 10, qrbox: { width: 250, height: 250 } };
    html5
      .start({ facingMode: 'environment' }, config, handleDecode, () => {})
      .then(() => {
        startedRef.current = true;
        setCameraError(false);
        // Hide upload option when camera successfully starts (unless desktop)
        if (!isDesktop) {
          setShowUploadOption(false);
        }
      })
      .catch(() => {
        setCameraError(true);
        setShowUploadOption(true); // Show upload when camera fails
        startedRef.current = false;
        scannerRef.current = null;
      });

    return () => {
      stopScanner();
    };
  }, [isOpen, isDesktop]);

  const onCloseClick = async () => {
    await stopScanner();
    onClose();
  };

  const onFileChange = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;

    // Validation
    if (file.size > MAX_FILE_BYTES) {
      toast.error('File too large (max 5MB)');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (file.type && !file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (handledRef.current) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setLookupLoading(true);
    
    try {
      // Stop camera scanner if it's running
      await stopScanner();

      // Create dedicated file scanner instance (don't reuse camera scanner)
      const fileScanner = new Html5Qrcode(SCANNER_DIV_ID);
      fileScannerRef.current = fileScanner;

      // Process file with timeout
      const decodePromise = fileScanner.scanFile(file, false);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Processing timeout')), 10000)
      );

      const decodedText = await Promise.race([decodePromise, timeoutPromise]);
      
      // Clear file input
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      // Clean up file scanner
      fileScannerRef.current = null;
      
      await handleDecode(decodedText);
    } catch (err) {
      // Provide more specific error messages
      let errorMessage = 'No QR code found in image';
      if (err.message === 'Processing timeout') {
        errorMessage = 'Image processing timed out. Please try a smaller or clearer image.';
      } else if (err.message?.includes('No QR code')) {
        errorMessage = 'No QR code found in image. Please ensure the image contains a clear QR code.';
      }
      
      toast.error(errorMessage);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fileScannerRef.current = null;
    } finally {
      setLookupLoading(false);
    }
  };

  const handleToggleUpload = () => {
    setShowUploadOption(!showUploadOption);
    // If enabling upload, stop camera to avoid conflicts
    if (!showUploadOption && startedRef.current) {
      stopScanner();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseClick}
      title="Scan asset QR code"
      size="md"
      theme="admin"
      footer={
        <button
          type="button"
          onClick={onCloseClick}
          className="btn btn-outline"
          disabled={lookupLoading}
        >
          Cancel
        </button>
      }
    >
      <div className="p-4 space-y-4">
          {/* Camera Scanner */}
          {!cameraError && (
            <div
              id={SCANNER_DIV_ID}
              className="w-full rounded-lg overflow-hidden bg-gray-100 min-h-[240px]"
            />
          )}

          {/* Camera Error Message */}
          {cameraError && !isDesktop && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Camera unavailable</p>
                <p className="text-xs text-amber-700 mt-1">
                  {showUploadOption 
                    ? 'Use the file upload below to scan from an image.' 
                    : 'Please enable camera permissions or use file upload instead.'}
                </p>
              </div>
            </div>
          )}

          {/* Desktop Message */}
          {isDesktop && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
              <Camera className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">No camera detected</p>
                <p className="text-xs text-blue-700 mt-1">
                  Upload an image file containing the QR code to scan.
                </p>
              </div>
            </div>
          )}

          {/* Upload Option Toggle (when camera is active) */}
          {!cameraError && !isDesktop && !showUploadOption && (
            <button
              type="button"
              onClick={handleToggleUpload}
              className="w-full text-left text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span>Can't use camera? Upload an image instead</span>
            </button>
          )}

          {/* File Upload Input (conditionally displayed) */}
          {showUploadOption && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                <Upload className="w-4 h-4" />
                <span>Upload QR code image</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onFileChange}
                disabled={lookupLoading}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-red-50 file:text-red-700 hover:file:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Upload QR code image"
              />
              {!isDesktop && !cameraError && (
                <button
                  type="button"
                  onClick={handleToggleUpload}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Use camera instead
                </button>
              )}
            </div>
          )}

          {/* Loading State */}
          {lookupLoading && (
            <p className="text-sm text-gray-500 text-center">Looking up asset…</p>
          )}
      </div>
    </Modal>
  );
};

export default AssetQRScannerModal;
